"use client"

import Link from "next/link"
import React from "react"
import posthog from "posthog-js"

// Ring buffer of recent unhandled promise rejections — the router-wedge bug
// swallows its failure silently, so if anything DOES reject we want it in the
// nav_watchdog_fired diagnostic event below. Module scope = registered once.
const recentRejections: string[] = []
if (typeof window !== "undefined") {
  window.addEventListener("unhandledrejection", (e) => {
    try {
      recentRejections.push(String(e.reason).slice(0, 200))
      if (recentRejections.length > 10) recentRejections.shift()
    } catch {
      /* ignore */
    }
  })
}

/**
 * Use this component to create a Next.js `<Link />` that persists the current country code in the url,
 * without having to explicitly pass it as a prop.
 */
const LocalizedClientLink = ({
  children,
  href,
  onClick,
  prefetch,
  ...props
}: {
  children?: React.ReactNode
  href: string
  className?: string
  onClick?: () => void
  passHref?: true
  [x: string]: any
}) => {
  // Watchdog fallback: in some sessions (Safari AND mobile Chrome, typically
  // landing from SMS/email deep links) the App Router's navigation queue wedges
  // silently — no console error, clicks just do nothing. Seen live 2026-08-19:
  // customers arriving from marketing links couldn't reach /checkout or /account.
  // If the URL hasn't changed 2s after a plain left-click, fall back to a full
  // page load so the click always lands. 2s: wedged-session fetches complete in <0.4s, so this is generous, while a legitimately slow
  // SPA navigation (cold serverless start) isn't restarted.
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    onClick?.()

    // Only plain left-clicks on same-origin, different-page destinations.
    if (
      e.defaultPrevented ||
      e.button !== 0 ||
      e.metaKey ||
      e.ctrlKey ||
      e.shiftKey ||
      e.altKey ||
      props.target === "_blank"
    ) {
      return
    }
    try {
      const dest = new URL(href, window.location.href)
      if (dest.origin !== window.location.origin) return
      const before = window.location.pathname + window.location.search
      if (dest.pathname + dest.search === before) return

      // Once a session has proven wedged (the watchdog fired), stop waiting:
      // navigate every later click as a full page load immediately. Worst case
      // becomes ONE 2s hiccup per broken session; healthy sessions never pay.
      let wedged = false
      try {
        wedged = window.sessionStorage.getItem("fp_router_wedged") === "1"
      } catch {
        /* ignore */
      }
      if (wedged) {
        e.preventDefault()
        window.location.assign(dest.href)
        return
      }

      window.setTimeout(() => {
        const now = window.location.pathname + window.location.search
        if (now === before) {
          // Diagnostic beacon BEFORE bailing out: did the router even issue the
          // RSC fetch for this navigation, did it complete, and did anything
          // reject silently? This is how we pin the root cause of the wedge.
          try {
            const rsc = (performance.getEntriesByType("resource") as PerformanceResourceTiming[])
              .filter((r) => r.name.includes("_rsc"))
              .slice(-6)
              .map((r) => ({
                url: r.name.replace(window.location.origin, "").slice(0, 100),
                started_ms_ago: Math.round(performance.now() - r.startTime),
                duration: Math.round(r.duration),
                completed: r.responseEnd > 0,
              }))
            posthog.capture("nav_watchdog_fired", {
              nav_href: href,
              nav_from: before,
              rsc_fetches: JSON.stringify(rsc),
              unhandled_rejections: JSON.stringify(recentRejections.slice(-5)),
              seconds_since_load: Math.round(performance.now() / 1000),
            })
          } catch {
            /* diagnostics must never block the rescue */
          }
          // Remember the wedge for the rest of this session so later clicks
          // hard-navigate instantly instead of waiting out the watchdog again.
          try {
            window.sessionStorage.setItem("fp_router_wedged", "1")
          } catch {
            /* ignore */
          }
          window.location.assign(dest.href)
        }
      }, 2000)
    } catch {
      /* malformed href — let Link handle it */
    }
  }

  // Country code is handled by middleware rewrite - no prefix needed for clean URLs
  // prefetch is destructured (not read from the spread) and placed AFTER {...props}:
  // the old `prefetch={props.prefetch ?? false} {...props}` only defaulted to false
  // because the spread happened to omit the key — any refactor normalizing props
  // would have silently flipped the whole site back to prefetch-on.
  return (
    <Link href={href} {...props} prefetch={prefetch ?? false} onClick={handleClick}>
      {children}
    </Link>
  )
}

export default LocalizedClientLink
